import React, { useState } from 'react';
import TableModelCell, { TableModelCellActions } from './TableModelCell';
import Pagination from './Pagination';
import { Product } from 'services/api/domains/products/types';
import { Manufacturer } from 'services/api/domains/manufacturers/types';
import { Facility } from 'services/api/domains/facilities/types';
import { TherapeuticArea } from 'services/api/domains/therapeutic-areas/types';
import { Grade } from 'services/api/domains/grades/types';
import TableCell from './TableCell';
import { Tooltip } from 'react-tooltip';
import { Info } from 'lucide-react';
import { AuditLog } from 'services/api/domains/audit/types';

export type LgmDataTableType =
  | Manufacturer
  | Facility
  | Product
  | TherapeuticArea
  | Grade
  | AuditLog;

export type Column<T> = {
  key: keyof T;
  label: string;
  render?: (row: T, isExpanded?: boolean) => React.ReactNode;
  textLength?: (row: T) => number;
  width?: string | number;
  tooltip?: string; // Optional tooltip text
};

// TODO: Fix key needing to be undefined
type SortConfig<T> = {
  key: keyof T | undefined;
  direction: 'ASC' | 'DESC';
};

export type LgmDataTableProps<T = LgmDataTableType> = {
  data: T[];
  columns: Column<T>[];
  sortConfig?: SortConfig<T> | null;
  onSort?: (sort: { key: keyof T; direction: 'ASC' | 'DESC' }) => void;
  paginationInfo?: {
    startIdx: number;
    endIdx: number;
    count: number;
    page: number;
    setPage?: (page: number) => void;
  };
  onInfo?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onMerge?: (row: T) => void;
  customRowStyle?: (row: T, index: number) => React.CSSProperties;
  customCellStyle?: (
    row: T,
    col: Column<T>,
    colIndex: number
  ) => React.CSSProperties;
  actions?: (row: T) => TableModelCellActions<T>[];
};

const LgmDataTable = <T extends LgmDataTableType>(
  props: LgmDataTableProps<T>
) => {
  const {
    data,
    columns,
    sortConfig,
    onSort,
    paginationInfo,
    onInfo,
    onEdit,
    onDelete,
    onMerge,
    actions,
    customRowStyle,
    customCellStyle,
  } = props;

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const renderSortArrow = (key: keyof T) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'ASC' ? ' ▲' : ' ▼';
  };

  return (
    <div style={styles.container}>
      <div style={styles.tableFixed}>
        <table style={styles.mainTableStyle}>
          <thead style={styles.stickyHeaderStyle}>
            <tr>
              {columns.map(col => (
                <th
                  key={String(col.key)}
                  onClick={() =>
                    onSort?.({
                      key: col.key,
                      direction:
                        sortConfig?.direction === 'ASC' ? 'DESC' : 'ASC',
                    })
                  }
                  style={{
                    ...styles.sortableHeaderStyle,
                    width: col.width || 'auto',
                  }}
                  title={col.tooltip || ''} // Add tooltip if provided
                >
                  {col.label}
                  {renderSortArrow(col.key)}
                  {col.tooltip && (
                    <span
                      data-tooltip-id={`tooltip-${String(col.key)}`}
                      data-tooltip-content={col.tooltip}
                      data-tooltip-place="top"
                      style={styles.tooltipIcon}>
                      <Info size={16} />
                    </span>
                  )}
                  <Tooltip
                    style={styles.tooltip}
                    id={`tooltip-${String(col.key)}`}
                  />
                </th>
              ))}
              {(onInfo || onEdit || onDelete || actions) && (
                <th style={styles.mainTableNonSortableHeaderStyle}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody style={styles.bodyStyle}>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={styles.emptyStateStyle}>
                  No data to display
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const isExpanded = expandedRows.has(rowIndex);
                return (
                  <tr
                    key={rowIndex}
                    style={customRowStyle ? customRowStyle(row, rowIndex) : {}}>
                    {columns.map((col, colIndex) => {
                      return (
                        <TableCell
                          key={colIndex}
                          row={row}
                          col={col}
                          colIndex={colIndex}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleRowExpansion(rowIndex)}
                          customCellStyle={customCellStyle}
                        />
                      );
                    })}

                    {(onInfo || onEdit || onDelete || onMerge || actions) && (
                      <TableModelCell
                        row={row}
                        onInfo={onInfo ? () => onInfo(row) : undefined}
                        onEdit={onEdit ? () => onEdit(row) : undefined}
                        onDelete={onDelete ? () => onDelete(row) : undefined}
                        onMerge={onMerge ? () => onMerge(row) : undefined}
                        actions={actions ? actions(row) : undefined}
                      />
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={styles.stickyPaginationStyle}>
        <Pagination
          startIdx={paginationInfo?.startIdx ?? 0}
          endIdx={paginationInfo?.endIdx ?? 0}
          count={paginationInfo?.count ?? 0}
          dateLength={data.length}
          page={paginationInfo?.page ?? 0}
          setPage={paginationInfo?.setPage}
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    maxHeight: 'fit-content',
  } as React.CSSProperties,
  tableFixed: {
    overflowY: 'scroll',
    overflowX: 'auto',
    height: 'fit-content',
    maxHeight: '80vh',
  } as React.CSSProperties,
  mainTableStyle: {
    width: '100%',
    borderCollapse: 'separate' as const,
    border: '1px solid #d1d5db', // border-gray-300
    tableLayout: 'fixed' as const,
  } as React.CSSProperties,
  stickyHeaderStyle: {
    position:
      'sticky' /* Makes the element stick when its position is reached */,
    top: 0 /* Sticks it to the top of the scrollable container (the div) */,
    zIndex: 10 /* Ensures the header is above the body content when scrolling */,
    backgroundColor:
      '#f8f9fa' /* Match your preferred header background for overlay effect */,
    boxShadow:
      '0 2px 2px -1px rgba(0, 0, 0, 0.2)' /* Optional: Adds a subtle shadow */,
  } as React.CSSProperties,
  sortableHeaderStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    textAlign: 'left' as const,
    cursor: 'pointer',
    position: 'relative', // Added for tooltip positioning
  } as React.CSSProperties,
  mainTableNonSortableHeaderStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
    textAlign: 'left' as const,
    width: 'auto',
  } as React.CSSProperties,
  bodyStyle: {
    top: 48,
    flex: 1,
    overflowY: 'auto',
  } as React.CSSProperties,
  emptyStateStyle: {
    padding: '1rem 1rem 0.75rem', // px-4 py-3
    textAlign: 'center',
    color: '#9ca3af', // text-gray-400
    fontStyle: 'italic',
  } as React.CSSProperties,
  stickyPaginationStyle: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#ffffff', // bg-white
    borderTop: '1px solid #d1d5db', // border-gray-300
    zIndex: 10,
  } as React.CSSProperties,
  tooltipIcon: {
    marginLeft: '8px',
    cursor: 'pointer',
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
  } as React.CSSProperties,
  tooltip: {
    zIndex: 20, // Ensure tooltip appears above other elements
  } as React.CSSProperties,
};

export default LgmDataTable;
